import { useEffect, useState, type FormEvent } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "../../../../components/common/button";
import { Input, PhoneInput } from "../../../../components/common/input";
import { useFormFields } from "../../../../hooks/useFormFields";
import { useSubmitForm } from "../../../../hooks/useSubmitForm";
import { getQueryParam } from "../../../../utils/urls";
import { showErrorToast } from "../../../../utils/toasts";
import { AppRoutes } from "../../../../common/routes/app_routes";
import { authPasswordService } from "../../../api_client";
import { validateNewPassword } from "../../../../utils/validation";

export type ResetPwFormValues = {
  password: string;
  password_confirmation: string;
  email?: string;
  phone_number?: string;
  token: string;
};

type ResetPasswordLocationState = {
  token?: string;
  email?: string;
  phone_number?: string;
} | null;

export function ResetPasswordForm() {
  const [token, setToken] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [phoneNumber, setPhoneNumber] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const { formFields, createChangeHandler } = useFormFields<ResetPwFormValues>({
    password: "",
    password_confirmation: "",
    email: "",
    phone_number: "",
    token: "",
  });

  const { isLoading, errors, submitHandler, success, setErrors } =
    useSubmitForm<ResetPwFormValues>({
      submitMethod: async () => {
        return authPasswordService.passwordReset({
          token: token ?? "",
          password: formFields.password,
        });
      },
      runAfterError: (error) => {
        showErrorToast("Error resetting password", error);
      },
      runAfterSuccess: () => {
        setTimeout(() => {
          navigate(AppRoutes.LOGIN);
        }, 2000);
      },
    });

  useEffect(() => {
    const state = (location.state as ResetPasswordLocationState) ?? null;
    const token = state?.token ?? getQueryParam("token");
    const email = state?.email ?? getQueryParam("email");
    const phone_number = state?.phone_number ?? getQueryParam("phone_number");

    if (!token) {
      navigate(AppRoutes.FORGOT_PASSWORD);
      return;
    }

    setToken(token);
    if (email) {
      setEmail(email);
    }
    if (phone_number) {
      const formattedPhone = phone_number.startsWith("+")
        ? phone_number
        : `+${phone_number}`.replace(" ", "");
      setPhoneNumber(formattedPhone);
    }
  }, [navigate, location.state]);

  if (!token) {
    return null;
  }

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const problem = validateNewPassword(
      formFields.password,
      formFields.password_confirmation,
    );
    if (problem) {
      setErrors({
        password: problem.password ? [problem.password] : undefined,
        password_confirmation: problem.confirmation
          ? [problem.confirmation]
          : undefined,
      });
      return;
    }
    setErrors(null);
    void submitHandler();
  };

  return success ? (
    <div>
      <p className="text-center mt-10 text-green-500">
        Your password has been changed
      </p>
      <div className="mt-4">
        <Button variant="primary" className="w-full" disabled>
          Redirecting to login...
        </Button>
      </div>
    </div>
  ) : (
    <form onSubmit={onSubmit} noValidate className="space-y-4 w-full">
      {email && (
        <Input
          label="Email address"
          type="email"
          name="email"
          placeholder="Email"
          autoComplete="email"
          required
          value={email}
          disabled
        />
      )}

      {phoneNumber && (
        <PhoneInput
          label="Phone Number"
          onChange={() => {}}
          value={phoneNumber}
          required
          disabled
        />
      )}

      <Input
        label="New password"
        type="password"
        name="password"
        placeholder="Enter your new password"
        autoComplete="new-password"
        required
        error={errors?.password?.[0]}
        value={formFields.password}
        onChange={createChangeHandler("password")}
      />

      <Input
        label="Confirm password"
        type="password"
        name="confirm_password"
        placeholder="Re-enter your new password"
        autoComplete="new-password"
        required
        error={errors?.password_confirmation?.[0]}
        value={formFields.password_confirmation}
        onChange={createChangeHandler("password_confirmation")}
      />

      {isLoading && <div className="my-4 text-center">Loading...</div>}

      <div className="mt-6">
        <Button variant="primary"
          className="w-full"
          disabled={isLoading}
          type="submit"
        >
          Reset Password
        </Button>
      </div>
    </form>
  );
}
